import type { Router } from 'vue-router'
import { ERouteNames } from '@/router/ERouteNames'
import {
  getChatAppPath,
  useChatStoreCoreBridge,
  useSlidingChatGlobalBridge,
} from '@/composables/useChatModuleBridge'
import { getActiveChatModule } from '@/environment/environment'

async function getAppRouter(): Promise<Router> {
  const { default: router } = await import('@/router')
  return router
}

/** Уже на странице мессенджера — открываем в основном UI, не в слайдере */
function isOnMessengerPage(router: Router): boolean {
  const name = router.currentRoute.value.name
  return name === ERouteNames.CHAT_MATRIX
}

/**
 * Открывает Matrix-чат на странице мессенджера или в боковой панели.
 */
export const openChat = async (
  userId?: string | null,
  chatId?: number | null,
  openInNewTab = false,
) => {
  const router = await getAppRouter()

  if (openInNewTab) {
    let href = `${getChatAppPath()}?slide=true`

    if (userId) {
      const encodedUserId = encodeURIComponent(String(userId))

      href += `&userId=${encodedUserId}`
    } else if (chatId) {
      href += `&chatId=${chatId}`
    }

    window.open(href, '_blank', 'noopener')
    return
  }

  if (getActiveChatModule() === 'matrix' && isOnMessengerPage(router)) {
    if (chatId) {
      const chatStore = useChatStoreCoreBridge()
      if (!chatStore.isInitialized) {
        await chatStore.initializeOnce()
      }

      const { useChatStore } = await import('@/modules/chat-matrix-fast/stores/chatViewStore')
      const viewStore = useChatStore()
      const chat = chatStore.chats.find((item: { id: number }) => item.id === chatId)
        ?? await viewStore.fetchChatById(chatId)

      if (chat) {
        await viewStore.ensureChatListTabForChat(chat)
        await viewStore.openExistingChat(chat, chat.id)
        useSlidingChatGlobalBridge().closeSlidingChat()
        return
      }
    }

    if (userId) {
      const { useChatStore } = await import('@/modules/chat-matrix-fast/stores/chatViewStore')
      await useChatStore().getChat(userId)
      useSlidingChatGlobalBridge().closeSlidingChat()
      return
    }
  }

  const { openSlidingChat } = useSlidingChatGlobalBridge()
  openSlidingChat(chatId, userId)
}

/** Данные о пользователе, которые уже известны на фронте. */
export interface OpenOrCreateChatHints {
  phone?: string
  fullName?: string
  avatar?: string | null
}

/**
 * Создает или открывает существующий диалог с пользователем.
 */
export const openOrCreateChat = async (
  userId: string | number,
  openInNewTab = true,
  hints?: OpenOrCreateChatHints,
): Promise<number> => {
  if (!userId) {
    throw new Error('User ID is required')
  }

  void hints

  try {
    const router = await getAppRouter()
    const normalizedUserId = String(userId)

    const chatStore = useChatStoreCoreBridge()

    if (!chatStore.isInitialized) {
      await chatStore.initializeOnce()
    }

    const chat = await chatStore.findOrCreateDirectChat(normalizedUserId, false, hints)

    if (openInNewTab) {
      const href = `${getChatAppPath()}?slide=true&chatId=${chat.id}`

      window.open(href, '_blank', 'noopener')
    } else if (getActiveChatModule() === 'matrix' && isOnMessengerPage(router)) {
      const { useChatStore } = await import('@/modules/chat-matrix-fast/stores/chatViewStore')
      const viewStore = useChatStore()
      await viewStore.ensureChatListTabForChat(chat)
      await viewStore.openExistingChat(chat, chat.id)
      useSlidingChatGlobalBridge().closeSlidingChat()
    } else {
      const { openSlidingChat } = useSlidingChatGlobalBridge()

      openSlidingChat(chat.id, null)
    }

    return chat.id
  } catch (error) {
    console.error('Failed to open or create chat:', error)
    throw error
  }
}

/**
 * Открывает полноэкранный чат
 */
export const openFullScreenChat = async (userId?: string | null) => {
  const router = await getAppRouter()
  const routeName = ERouteNames.CHAT_MATRIX

  if (userId) {
    const href = router.resolve({
      name: routeName,
      params: { param: String(userId) },
    }).href

    await router.push(href)
  } else {
    await router.push({ name: routeName })
  }
}
